import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { meApi } from '../../api/endpoints/me';
import { handleApiError } from '../../api/errorMapping';
import { useAuthStore } from '../../stores/authStore';
import { useMyTenantIdentifier } from '../../hooks/useMyTenantIdentifier';
import { Button, Input } from '@/kit';
import { optionalNameField, optionalPhoneField } from '../../lib/formValidators';

const schema = z.object({
    displayName: optionalNameField,
    phone: optionalPhoneField,
});

type FormValues = z.infer<typeof schema>;

export function UpdateProfileForm() {
    const user = useAuthStore((s) => s.user);
    const refresh = useAuthStore((s) => s.refresh);
    // DEC-RBAC-079/081: "Mi perfil" muestra SOLO el identificador PRINCIPAL de login. El secundario
    // (el opuesto) se ve y gestiona en su propia tarjeta (SecondaryIdentifierSwitch).
    const { principalIdentifier } = useMyTenantIdentifier();
    const isPhonePrincipal = principalIdentifier === 'PHONE';
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        mode: 'onTouched',
        defaultValues: { displayName: user?.displayName ?? '', phone: user?.phone ?? '' },
    });

    const mut = useMutation({
        mutationFn: (v: FormValues) => meApi.updateProfile(v),
        onSuccess: async (_data, variables) => {
            await refresh();
            form.reset(variables); // re-baseline → isDirty vuelve a false (oculta Guardar)
            toast.success('Perfil actualizado');
        },
        onError: handleApiError,
    });

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            {/* Identificador PRINCIPAL de login (el secundario vive en su propia tarjeta). Si el tenant es
                phone-primary → Teléfono (editable, validado); si es email-primary → Email (no editable). */}
            {isPhonePrincipal ? (
                <Input
                    id="phone"
                    label="Teléfono"
                    type="tel"
                    inputMode="tel"
                    hint="10 dígitos, o +52 con código de país"
                    error={form.formState.errors.phone?.message}
                    {...form.register('phone')}
                />
            ) : (
                <Input
                    id="email"
                    label="Email"
                    type="email"
                    value={user?.email ?? ''}
                    disabled
                    readOnly
                />
            )}
            <Input
                id="displayName"
                label="Nombre"
                error={form.formState.errors.displayName?.message}
                {...form.register('displayName')}
            />
            {form.formState.isDirty && (
                <Button type="submit" disabled={mut.isPending}>
                    {mut.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
            )}
        </form>
    );
}
